import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  LaunchPage,
  MaterialUiTable,
  sortByAlphabeticalOrder,
  Badge,
  getBadgeColorByScore,
  Avatar,
  Spacer,
  TableTagBoolean,
} from "@qivia/ui";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  DriverElectrification,
  DriverElectrificationDisplayed,
  ElectroCompatibleDriver,
} from "./electrificationApi";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import styled from "styled-components";
import {
  selectDriversList,
  selectDriversListStatus,
} from "../drivers/driversSlice";
import "../../../i18n/i18n";

const fakeData: ElectroCompatibleDriver[] = [
  {
    prioritizationScore: 88,
    electroCompatible: true,
    drivingScore: 82,
    homeCharging: true,
  },
  {
    prioritizationScore: 55,
    electroCompatible: true,
    drivingScore: 61,
    homeCharging: false,
  },
  {
    prioritizationScore: 24,
    electroCompatible: false,
    drivingScore: 30,
    homeCharging: false,
  },
  {
    prioritizationScore: 72,
    electroCompatible: true,
    drivingScore: 45,
    homeCharging: true,
  },
  {
    prioritizationScore: 90,
    electroCompatible: false,
    drivingScore: 78,
    homeCharging: true,
  },
];

export const DriversTab = () => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const electroCompatibleDriversListInit: ElectroCompatibleDriver[] = fakeData;
  const driversListInit = useAppSelector(selectDriversList);
  const driversListStatus = useAppSelector(selectDriversListStatus);

  const [listDisplayed, setListDisplayed] = useState<
    DriverElectrificationDisplayed[] | null
  >(null);

  const pageName = "elecDrivers";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      name: "size",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const driversElectrificationList: DriverElectrification[] = useMemo(() => {
    const driversElectrification: DriverElectrification[] = driversListInit
      .slice(0, fakeData.length)
      .map((driver, index) => {
        // Fake data matched by index (not uuid) so it renders the same in
        // local and demo, where driver uuids differ.
        const driverElectrification =
          electroCompatibleDriversListInit[
            index % electroCompatibleDriversListInit.length
          ];
        return {
          uuid: driver.uuid,
          name: driver.name,
          prioritizationScore: driverElectrification.prioritizationScore,
          electroCompatible: driverElectrification.electroCompatible,
          drivingScore: driverElectrification.drivingScore,
          homeCharging: driverElectrification.homeCharging,
        };
      });

    const rowsWithId = driversElectrification.map((av) => {
      const newDict: DriverElectrification & { id: string } = {
        ...av,
        id: av.uuid,
      };
      return newDict;
    });
    return sortByAlphabeticalOrder(rowsWithId, "asc", "name");
  }, [driversListInit, electroCompatibleDriversListInit]);

  useEffect(() => {
    if (driversListStatus === "success" && driversElectrificationList) {
      //TODO use later electroCompatibleDriversListStatus

      const driversSorted = sortByAlphabeticalOrder(
        driversElectrificationList,
        "asc",
        "name",
      );
      setListDisplayed(driversSorted);
    }
  }, [driversElectrificationList, driversListStatus]);

  const searchBarProps = {
    values: driversElectrificationList,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("drivers.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof DriverElectrificationDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 220,
        valueFormatter: (value: string) => {
          if (!value) return <>-</>;
          const nameParts = value.split(" ");
          const firstName = nameParts[0];
          const lastName = nameParts[nameParts.length - 1];
          return (
            <StyledRow>
              <Avatar
                size={"S"}
                color={"green"}
                firstName={firstName}
                lastName={lastName}
              />
              <Spacer x={0.5} />
              {value}
            </StyledRow>
          );
        },
      },
      prioritizationScore: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) =>
          value != null && (
            <StyledRow>
              <Badge
                text={String(value)}
                backgroundColor={getBadgeColorByScore(
                  "prioritizationScore",
                  value,
                )}
                textColor={colors["colors/text/black"]}
              />
            </StyledRow>
          ),
      },
      electroCompatible: {
        type: "string",
        width: 200,
        valueFormatter: (value: boolean) => (
          <StyledRow>
            <TableTagBoolean value={value} />
          </StyledRow>
        ),
      },
      drivingScore: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) =>
          value != null && (
            <StyledRow>
              <Badge
                text={String(value)}
                backgroundColor={getBadgeColorByScore("ecoDrivingScore", value)}
                textColor={colors["colors/text/black"]}
              />
            </StyledRow>
          ),
      },
      homeCharging: {
        type: "string",
        width: 200,
        valueFormatter: (value: boolean) => (
          <StyledRow>
            <TableTagBoolean value={value} />
          </StyledRow>
        ),
      },
    };
  }, []);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "drivers") {
    return;
  }

  return (
    <MaterialUiTable<
      keyof DriverElectrificationDisplayed,
      DriverElectrificationDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;
